import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, CreditCard, ExternalLink, Receipt, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanEntitlement } from '../../hooks/usePlanEntitlement';
interface BillingManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}
interface BillingRecord {
  date: string;
  plan?: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  description?: string;
  reference?: string;
  receipt_url?: string;
}
const STATUS_STYLE: Record<BillingRecord['status'], string> = {
  paid: 'border-green-500/40 bg-green-500/10 text-green-400',
  pending: 'border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700]',
  failed: 'border-[#FF0000]/40 bg-[#FF0000]/10 text-[#FF0000]'
};
/**
 * Billing history for the authenticated user. Records come from the billing
 * Edge Function only — nothing is fabricated locally, so an account with no
 * payments shows a clean empty state.
 */
export function BillingManagementModal({
  isOpen,
  onClose
}: BillingManagementModalProps) {
  const {
    user
  } = useAuth();
  const {
    planLabel,
    priceLabel,
    statusLabel,
    renewalDate
  } = usePlanEntitlement();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<BillingRecord[]>([]);
  const fetchBillingData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`https://neadfoxscgjeekvabnoz.supabase.co/functions/v1/billing-history?user_id=${user.id}`);
      if (!response.ok) throw new Error('Request failed');
      const data = await response.json();
      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setHistory([]);
      setError('Could not load billing history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);
  useEffect(() => {
    if (isOpen) fetchBillingData();
  }, [isOpen, fetchBillingData]);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-1633" data-mp="16njaibvnl6m6">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6" data-id="element-1634" data-mp="1bk9pswzmvy6j">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="absolute inset-0 bg-black/85 backdrop-blur-sm" data-id="element-1635" data-mp="1gjdxfj6wtnur" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="billing-modal-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto border border-gray-800 bg-[#050505] p-6 sm:p-8" data-id="element-1636" data-mp="l9hc8tldafdv">
            <button type="button" onClick={onClose} aria-label="Close billing history" className="absolute right-4 top-4 p-2 text-gray-500 transition-colors duration-150 ease-out hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-1637" data-mp="3ju97wosghay5">
              <X size={18} data-id="element-1638" data-mp="8c32a1zw56fc" />
            </button>

            <header className="mb-7 pr-10" data-id="element-1639" data-mp="8ars9kfur2fs">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-1640" data-mp="3qq28m02r7xkw">
                Transaction log
              </p>
              <h2 id="billing-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-1641" data-mp="3uqcoq5k9g4dh">
                Billing History
              </h2>
            </header>

            {/* Current plan summary */}
            <div className="mb-7 border border-gray-800 bg-[#0a0a0a] p-5" data-id="element-1642" data-mp="37rztcd00df0a">
              <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1643" data-mp="3bsjm74djtwgm">
                <div data-id="element-1644" data-mp="3bumlyg4i73yj">
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-1645" data-mp="15zbl1p8ic1dn">
                    Current plan
                  </p>
                  <p className="mt-1.5 text-lg font-black uppercase tracking-tight text-white" data-id="element-1646" data-mp="1a0ncm0axywoh">
                    {planLabel}
                  </p>
                  <p className="mt-1 font-mono text-xs text-gray-400" data-id="element-1647" data-mp="2qifbcjwviiy0">
                    {priceLabel} · {statusLabel}
                  </p>
                </div>
                {renewalDate && <div className="flex items-center gap-2 font-mono text-xs text-gray-400" data-id="element-1648" data-mp="2zi23ef3pqcee">
                    <Calendar size={14} className="text-gray-600" data-id="element-1649" data-mp="3e6ylz63eb7t9" />
                    Renews {renewalDate}
                  </div>}
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-gray-800 pt-4" data-id="element-1650" data-mp="39ahpnom1ln53">
                <div className="flex items-center gap-3" data-id="element-1651" data-mp="3n5sf2mhipg22">
                  <CreditCard size={16} className="text-gray-600" data-id="element-1652" data-mp="3iad3k16zfw8t" />
                  <div data-id="element-1653" data-mp="1stlq8kvpb0ra">
                    <p className="font-mono text-xs text-white" data-id="element-1654" data-mp="1sv6ke9e3shom">
                      Payment method
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-wide text-gray-600" data-id="element-1655" data-mp="1nwva5xjwc55v">
                      Managed via Paystack
                    </p>
                  </div>
                </div>
                <a href="https://paystack.com/pay/update-payment" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF0000] transition-colors duration-150 ease-out hover:text-white" data-id="element-1656" data-mp="3syh8ybh9jou1">
                  Update
                  <ExternalLink size={12} data-id="element-1657" data-mp="fo0f8txdp5dp" />
                </a>
              </div>
            </div>

            {loading ? <p className="border border-gray-800 bg-[#0a0a0a] py-10 text-center font-mono text-xs uppercase tracking-widest text-gray-500" data-id="element-1658" data-mp="pfo88z9l2uha">
                Loading billing history…
              </p> : error ? <div className="border border-[#FF0000]/30 bg-[#FF0000]/5 p-6 text-center" data-id="element-1659" data-mp="yfkteqwrlb7l">
                <p className="font-mono text-xs text-[#FF0000]" data-id="element-1660" data-mp="2iywnmnz9znr0">{error}</p>
                <button type="button" onClick={fetchBillingData} className="mt-4 border border-gray-700 px-5 py-2.5 font-mono text-[10px] font-black uppercase tracking-[0.15em] text-white transition-colors duration-200 ease-out hover:bg-white/5" data-id="element-1661" data-mp="2dza9mcbmi3h5">
                  Retry
                </button>
              </div> : history.length === 0 ? <div className="border border-gray-800 bg-[#0a0a0a] px-6 py-12 text-center" data-id="element-1662" data-mp="2xmrjgd8ky88b">
                <Receipt size={28} aria-hidden="true" className="mx-auto text-gray-700" data-id="element-1663" data-mp="2sw1akq0y3n8v" />
                <p className="mt-4 text-sm font-black uppercase tracking-wide text-white" data-id="element-1664" data-mp="2nvdngffu7qjx">
                  No payments yet
                </p>
                <p className="mt-2 font-mono text-xs text-gray-500" data-id="element-1665" data-mp="3gllyzofvbcpj">
                  Your completed purchases will appear here.
                </p>
              </div> : <ul className="border border-gray-800" data-id="element-1666" data-mp="2tp0y3e9xyryt">
                {history.map((item, index) => <li key={item.reference ?? `${item.date}-${index}`} className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-900 bg-[#0a0a0a] p-4 last:border-b-0" data-id="element-1667" data-mp="2f0wmeu9b3gpj" data-mpi={index}>
                    <div className="min-w-0" data-id="element-1668" data-mp="37pv4jrdfiiuu">
                      <p className="text-sm font-bold text-white" data-id="element-1669" data-mp="3coolxuvu6u0a">
                        {item.plan || item.description || 'Purchase'}
                      </p>
                      <p className="mt-1 font-mono text-[11px] text-gray-500" data-id="element-1670" data-mp="2yqggszoncme1">
                        {new Date(item.date).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                        {item.reference ? ` · ${item.reference}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-1671" data-mp="1espv4z0i3n89">
                      <span className="font-mono text-sm font-bold text-white" data-id="element-1672" data-mp="1euaoilb2x04j">
                        ${item.amount.toFixed(2)}
                      </span>
                      <span className={`border px-2 py-1 font-mono text-[9px] font-black uppercase tracking-[0.15em] ${STATUS_STYLE[item.status] ?? STATUS_STYLE.pending}`} data-id="element-1673" data-mp="1orfgj1rlvpt9">
                        {item.status}
                      </span>
                      {item.receipt_url && <a href={item.receipt_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 transition-colors duration-150 ease-out hover:text-white" data-id="element-1674" data-mp="1asphhgl87g29">
                          Receipt
                        </a>}
                    </div>
                  </li>)}
              </ul>}

            <button type="button" onClick={onClose} className="mt-7 w-full border border-gray-700 py-3 font-mono text-[11px] font-black uppercase tracking-[0.15em] text-white transition-colors duration-200 ease-out hover:bg-white/5" data-id="element-1675" data-mp="366vl25mbt9w2">
              Close
            </button>
          </motion.div>
        </div>}
    </AnimatePresence>;
}